/**
 * Calidad de datos — métricas globales + reglas + DMA breakdown.
 *
 * Vigila los problemas que silenciosamente envenenan el cálculo de
 * NRW: duplicados, drift de RTC, gaps, lecturas imposibles, timezone
 * mismatches.  Cada regla muestra su impacto y cuántos eventos se
 * dispararon en las últimas 24 h.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv } from "../lib/exporters";
import { VirtualTable, type VirtualColumn } from "../components/VirtualTable";
import { SkeletonTable } from "../components/Skeleton";

interface DqMetrics {
  cobertura_lecturas_pct: number;
  duplicados: number;
  rtc_drift: number;
  gaps: number;
  timezone_issues: number;
  lecturas_imposibles: number;
  por_dma: Array<{ dma_codigo: string; cobertura_pct: number; gaps: number; alertas: number }>;
  reglas: Array<{ codigo: string; descripcion: string; impacto: string; eventos_24h: number }>;
  _source?: string;
}

function impactoTone(impacto: string): "accent" | "warn" | "danger" | "neutral" {
  if (impacto === "alto") return "danger";
  if (impacto === "medio") return "warn";
  if (impacto === "bajo") return "accent";
  return "neutral";
}

type ReglaRow = DqMetrics["reglas"][number];

const REGLA_COLUMNS: VirtualColumn<ReglaRow>[] = [
  { key: "codigo", header: "Código", className: "text-mono", cell: (r) => r.codigo },
  { key: "descripcion", header: "Descripción", cell: (r) => r.descripcion },
  {
    key: "impacto",
    header: "Impacto",
    cell: (r) => <span className={`badge badge--${impactoTone(r.impacto)}`}>{r.impacto}</span>,
  },
  { key: "eventos_24h", header: "Eventos 24h", className: "numeric", cell: (r) => r.eventos_24h },
];

export function DataQuality(): JSX.Element {
  const { toast } = useToast();
  const dqQuery = useQuery<DqMetrics>({
    queryKey: ["quality.metrics"],
    queryFn: () => api<DqMetrics>("/quality/metrics"),
    staleTime: 60_000,
  });
  const m = dqQuery.data;
  const liveRf = m?._source === "rf-api";

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Calidad de datos"
        subtitle="Eventos automáticos que detectan problemas antes de contaminar el cálculo de NRW."
        actions={
          <>
            {liveRf && (
              <span className="badge badge--accent" title="Calidad de la telemetría LoRaWAN derivada del rf-api (DuckDB)">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--c-success)",
                    display: "inline-block",
                    marginRight: 5,
                  }}
                />
                EN VIVO · rf-api
              </span>
            )}
            <button
              className="btn"
              onClick={() => {
                const ok = exportRowsToCsv(
                  `reglas-calidad-${new Date().toISOString().slice(0, 10)}.csv`,
                  (m?.reglas ?? []).map((r) => ({
                    codigo: r.codigo,
                    descripcion: r.descripcion,
                    impacto: r.impacto,
                    eventos_24h: r.eventos_24h,
                  })),
                );
                toast(ok ? "Reglas exportadas a CSV" : "Sin reglas para exportar", ok ? "success" : "warn");
              }}
            >
              <Icon name="report" size={13} /> Exportar reglas
            </button>
          </>
        }
      />

      {!m && (
        <section className="card" style={{ marginBottom: 12 }}>
          <div className="card__title">
            <span>Cargando métricas de calidad…</span>
          </div>
          <SkeletonTable rows={6} columns={4} />
        </section>
      )}

      {m && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--accent">
              <span className="kpi__label">Cobertura lecturas</span>
              <span className="kpi__value">{m.cobertura_lecturas_pct.toFixed(1)} %</span>
              <span className="kpi__hint">vs lecturas esperadas</span>
            </div>
            <div className="kpi kpi--warn">
              <span className="kpi__label">Gaps detectados</span>
              <span className="kpi__value">{m.gaps}</span>
              <span className="kpi__hint">huecos temporales en serie</span>
            </div>
            <div className="kpi kpi--danger">
              <span className="kpi__label">Lecturas imposibles</span>
              <span className="kpi__value">{m.lecturas_imposibles}</span>
              <span className="kpi__hint">deltas físicamente inválidos</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">RTC drift</span>
              <span className="kpi__value">{m.rtc_drift}</span>
              <span className="kpi__hint">medidores con reloj desviado</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Duplicados</span>
              <span className="kpi__value">{m.duplicados}</span>
              <span className="kpi__hint">resueltos por hash menor</span>
            </div>
            <div className="kpi kpi--magenta">
              <span className="kpi__label">Timezone</span>
              <span className="kpi__value">{m.timezone_issues}</span>
              <span className="kpi__hint">eventos sin zona horaria</span>
            </div>
          </div>

          <section className="card" style={{ marginBottom: 12 }}>
            <div className="card__title">
              <span>Reglas activas (24 h)</span>
            </div>
            <VirtualTable<ReglaRow>
              rows={m.reglas}
              columns={REGLA_COLUMNS}
              rowKey={(r) => r.codigo}
            />
          </section>

          <section className="card">
            <div className="card__title">
              <span>{liveRf ? "Cobertura por gateway" : "Cobertura por DMA"}</span>
            </div>
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{liveRf ? "Gateway" : "DMA"}</th>
                    <th className="numeric">Cobertura</th>
                    <th className="numeric">Gaps</th>
                    <th className="numeric">Alertas DQ</th>
                  </tr>
                </thead>
                <tbody>
                  {m.por_dma.map((r) => {
                    const tone = r.cobertura_pct >= 96 ? "accent" : r.cobertura_pct >= 92 ? "warn" : "danger";
                    return (
                      <tr key={r.dma_codigo}>
                        <td><strong className={liveRf ? "text-mono" : ""}>{r.dma_codigo}</strong></td>
                        <td className="numeric">
                          <span className={`badge badge--${tone}`}>{r.cobertura_pct.toFixed(1)} %</span>
                        </td>
                        <td className="numeric">{r.gaps}</td>
                        <td className="numeric">{r.alertas}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
